import api, { apiErrorHandler } from "api";
import { Dispatch } from "redux";
import { ShiftReportsConstants } from "constants/constants";
import { RootState } from "reducers/rootReducer";
import { customToast } from "hooks/toast";

export const getReportsAction =
  (
    page: number,
    rows: number,
    station?: number | null,
    created_by?: number | null,
    edited_by?: number | null,
    date_shift?: string | "",
    date_range?: string[] | [],
    year?: number | null,
    week?: string | "",
    month?: string | "",
    driver?: number | null,
    fleet_number?: number | null,
    fleet_group?: number | null,
    fleet_size?: number | null,
    rabbit_number?: number | null,
    dolly_number?: number | null,
  ) =>
  async (dispatch: Dispatch) => {
    dispatch({ type: ShiftReportsConstants.SHIFT_REPORT_GET_LIST_PENDING });
    api
      .getReports({
        page: page,
        page_size: rows,
        driver,
        ...(station && { station }),
        ...(created_by && { created_by }),
        ...(edited_by && { edited_by }),
        ...(date_shift && { date_shift }),
        ...(date_range && { date_from: date_range[0] }),
        ...(date_range && { date_to: date_range[1] }),
        ...(year && { year: Number(year) }),
        ...(week && { week }),
        ...(month && { month }),
        ...(rabbit_number && { rabbit_number }),
        ...(dolly_number && { dolly_number }),
        ...(fleet_number && { fleet_number }),
        ...(fleet_number && { fleet_number }),
        ...(fleet_group && { fleet_group }),
        ...(fleet_size && { fleet_size }),
      })
      .then((res) => {
        if (res.data) {
          dispatch({
            type: ShiftReportsConstants.SHIFT_REPORT_GET_LIST_SUCCESS,
            payload: res.data,
          });
        }
      })
      .catch(
        apiErrorHandler(() => {
          dispatch({ type: ShiftReportsConstants.SHIFT_REPORT_GET_LIST_ERROR });
        }),
      );
  };

export const shiftTrackerUpdateFilters =
  (data: any) => async (dispatch: Dispatch, getStore: () => RootState) => {
    const { reportListFilters } = getStore()?.dasBoard;
    dispatch({
      type: ShiftReportsConstants.SHIFT_REPORT_UPDATE_FILTERS,
      payload: { ...reportListFilters, ...data },
    });
  };

export const shiftRecordsUpdateFilters =
  (data: any) => async (dispatch: Dispatch, getStore: () => RootState) => {
    const { recordListFilters } = getStore()?.dasBoard;
    dispatch({
      type: ShiftReportsConstants.SHIFT_RECORD_RECORD_UPDATE_FILTERS,
      payload: { ...recordListFilters, ...data },
    });
  };

export const CreateReportAction =
  (
    data: {
      station: number;
      date: string;
      rows: number;
      hasSecondDriver: boolean;
    },
    onSuccess: (id: number) => void,
    onError?: () => void,
  ) =>
  (dispatch: Dispatch) => {
    dispatch({ type: ShiftReportsConstants.SHIFT_REPORT_CREATE_PENDING });
    api
      .createReport({
        station: data.station,
        report_date: data.date,
        ...(data.rows ? { rows: data.rows } : null),
        ...(data?.hasSecondDriver ? { has_second_driver: data?.hasSecondDriver } : null),
      })
      .then((res) => {
        if (res.data) {
          customToast("success", "Shift is created!");
          dispatch({
            type: ShiftReportsConstants.SHIFT_REPORT_CREATE_SUCCESS,
            payload: res.data,
          });
          onSuccess(res.data.id);
        }
      })
      .catch(
        apiErrorHandler(() => {
          customToast("error", "The date has expired, please re-create the report!");
          dispatch({ type: ShiftReportsConstants.SHIFT_REPORT_CREATE_ERROR });
          if (onError) onError();
        }),
      );
  };

export const updateReportAction = (reportId: number, data: any) => async (dispatch: Dispatch) => {
  dispatch({ type: ShiftReportsConstants.SHIFT_REPORT_UPDATE_PENDING });
  api
    .updateReport(reportId, data)
    .then((res) => {
      if (res.data) {
        dispatch({
          type: ShiftReportsConstants.SHIFT_REPORT_UPDATE_SUCCESS,
          payload: res.data,
        });
      }
    })
    .catch(
      apiErrorHandler(() => {
        dispatch({ type: ShiftReportsConstants.SHIFT_REPORT_UPDATE_ERROR });
      }),
    );
};

export const removeReportAction =
  (id: number) => async (dispatch: Dispatch, getStore: () => RootState) => {
    const { reportList } = getStore()?.dasBoard;
    const updatedData = reportList.filter((el) => el.id !== id);
    dispatch({ type: ShiftReportsConstants.SHIFT_REPORT_DELETE_PENDING });
    api
      .deleteReport(id)
      .then((res) => {
        dispatch({
          type: ShiftReportsConstants.SHIFT_REPORT_DELETE_SUCCESS,
          payload: updatedData,
        });
      })
      .catch(
        apiErrorHandler(() => {
          dispatch({ type: ShiftReportsConstants.SHIFT_REPORT_DELETE_ERROR });
        }),
      );
  };

export const updateReportCommentAction =
  (reportId: number, data: any) => async (dispatch: Dispatch, getStore: () => RootState) => {
    dispatch({ type: ShiftReportsConstants.SHIFT_REPORT_COMMENT_UPDATE_PENDING });
    const { reportList } = getStore()?.dasBoard;
    api
      .updateReport(reportId, data)
      .then((res) => {
        if (res.data) {
          const newDataList = reportList.map((item) =>
            item.id === res.data.id ? { ...item, ...data } : item,
          );

          dispatch({
            type: ShiftReportsConstants.SHIFT_REPORT_COMMENT_UPDATE_SUCCESS,
            payload: newDataList,
          });
        }
      })
      .catch(
        apiErrorHandler(() => {
          dispatch({ type: ShiftReportsConstants.SHIFT_REPORT_COMMENT_UPDATE_ERROR });
        }),
      );
  };

export const getRecordAction = (reportId: number) => async (dispatch: Dispatch) => {
  dispatch({ type: ShiftReportsConstants.SHIFT_RECORD_GET_PENDING, payload: reportId });
  api
    .getReport(reportId)
    .then((res) => {
      if (res.data) {
        dispatch({
          type: ShiftReportsConstants.SHIFT_RECORD_GET_SUCCESS,
          payload: res.data,
        });
      }
    })
    .catch(
      apiErrorHandler(() => {
        dispatch({
          type: ShiftReportsConstants.SHIFT_RECORD_GET_ERROR,
          payload: reportId,
        });
      }),
    );
};

export const createReportRecordAction =
  (recordId: number | null) => (dispatch: Dispatch, getStore: () => RootState) => {
    const { currentReport } = getStore()?.dasBoard;
    if (recordId) {
      api
        .createReportRecord(recordId)
        .then((res) => {
          dispatch({
            type: ShiftReportsConstants.SHIFT_RECORD_ADD_ROW_RECORD,
            payload: [...currentReport, { ...res.data }],
          });
        })
        .catch(
          apiErrorHandler(() => {
            dispatch({ type: ShiftReportsConstants.SHIFT_REPORT_GET_LIST_ERROR });
          }),
        );
    }
  };

export const updateReportRecordAction =
  (recordId: number, data: any) => (dispatch: Dispatch, getStore: () => RootState) => {
    const { currentReport } = getStore()?.dasBoard;
    api
      .updateReportRecord(recordId, data)
      .then((res) => {
        if (res.data) {
          const updatedData = currentReport.map((el) =>
            el.id === recordId ? { ...el, ...res.data } : el,
          );
          dispatch({
            type: ShiftReportsConstants.SHIFT_RECORD_UPDATE_RECORD_FIELD,
            payload: updatedData,
          });
        }
      })
      .catch(
        apiErrorHandler(() => {
          customToast("error", "The date has expired, please re-create the report!");
        }),
      );
  };

export const updateReportRecordStateAction =
  (recordId: number, data: any) => (dispatch: Dispatch, getStore: () => RootState) => {
    const { currentReport } = getStore()?.dasBoard;
    const updatedData = currentReport.map((el) => (el.id === recordId ? { ...el, ...data } : el));
    dispatch({
      type: ShiftReportsConstants.SHIFT_RECORD_UPDATE_RECORD_FIELD,
      payload: updatedData,
    });
  };

export const updateSerialNumberAction =
  (
    fieldName: "dolly_number" | "rabbit_number" | "second_dolly_number" | "second_rabbit_number",
    rowId: number,
    data: {
      rabbit_number?: number;
      second_rabbit_number?: number;
      dolly_number?: number;
      second_dolly_number?: number;
    },
    serialNumberlist: any[],
  ) =>
  (dispatch: Dispatch, getStore: () => RootState) => {
    const { currentReport } = getStore()?.dasBoard;
    api
      .updateSerialNumber({
        record_id: rowId,
        ...data,
      })
      .then((res: any) => {
        if (res?.data) {
          const updatedData = {
            ...(data[fieldName]
              ? { [fieldName]: serialNumberlist.find((item) => item.id === data[fieldName]) }
              : data[fieldName] === 0
                ? { [fieldName]: null }
                : null),
          };
          const updatedDataList = currentReport.map((el) =>
            el.id === rowId ? { ...el, ...updatedData } : el,
          );

          dispatch({
            type: ShiftReportsConstants.SHIFT_RECORD_UPDATE_RECORD_FIELD,
            payload: updatedDataList,
          });
        }
      })
      .catch(() => {});
  };

export const notifyReportDriversAction =
  (id: number, type: string) => async (dispatch: Dispatch) => {
    api
      .sendMessageDrivers({ da_tracker_id: id, message_type: type })
      .then(() => {
        customToast("success", "Message to drivers has been send!");
      })
      .catch(
        apiErrorHandler(() => {
          customToast("error", "Something was wrong!");
        }),
      );
  };

export const removeReportRecordAction =
  (id: number) => (dispatch: Dispatch, getStore: () => RootState) => {
    const { currentReport } = getStore()?.dasBoard;
    api
      .deleteReportRecord(id)
      .then(() => {
        const newData = currentReport.filter((el) => el.id !== id);
        dispatch({
          type: ShiftReportsConstants.SHIFT_RECORD_DELETE_RECORD_FIELD,
          payload: [...newData],
        });
      })
      .catch(
        apiErrorHandler(() => {
          dispatch({ type: ShiftReportsConstants.SHIFT_REPORT_GET_LIST_ERROR });
        }),
      );
  };
